/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React from "react";
import PropTypes from "prop-types";
import Col from "../../../common/core/Col";
import Button from "../../../common/core/Button";
import Input from "../../../common/core/Input";
import Label from "../../../common/core/Label";
import ValidationConstants from "../../../common/utils/validationConstants";
import Form from "../../../common/core/Form";
import FormGroup from "../../../common/core/FormGroup";
import FormFeedback from "../../../common/core/FormFeedback";
import Spinner from "../../../common/core/Spinner";
import Alert from "../../../common/core/Alert";
import Constants from "../../../common/utils/Constants";

const Edit = (props) => {
  const {
    handleClose,
    handleSave,
    handleEditCustomer,
    customer,
    methods,
    message,
    editCustomerInProgress,
    companyId,
  } = props;

  const { AVAILABLE_COMPANIES } = Constants;
  const {
    register, errors, handleSubmit,
  } = methods;

  const {
    IS_CLASSIFICATION, IS_EMAIL, IS_ALPHABETIC, IS_NUMBER, IS_DIAL_CODE,
  } = ValidationConstants;

  return (
    <>
      <div className="edit">
        <div className="edit-head">
          <h5>
            Edit Customer Account: &nbsp;
            <span>{customer.accountNo}</span>
          </h5>
          <Button color="transparent" onClick={() => handleClose()}>
            <i className="fal fa-times"> </i>
          </Button>
        </div>
        <div className="edit-subhead">
          Company: &nbsp;
          <span>{AVAILABLE_COMPANIES[companyId]}</span>
        </div>
        {/* <div className="alert-outer">
          <Alert color="warning">
            <span>
              Failed to save customer details!
            </span>
            <Button color="transparent">
              <i className="fal fa-times"> </i>
            </Button>
          </Alert>
        </div> */}
        <div className="edit-body">
          <div className="edit-content">
            <Form onSubmit={handleSubmit(handleSave)}>
              {editCustomerInProgress
                ? (
                  <div className="d-flex align-items-center justify-content-center">
                    <Spinner />
                  </div>
                )
                : (
                  <>
                    {
                      message && message.type === "warning" && (
                        <Alert color={message.type}>
                          {message.message}
                        </Alert>
                      )
                    }
                  </>
                )}
              <fieldset disabled={editCustomerInProgress}>
                <div className="edit-block">
                  <Col md="6" className="edit-item mb-3">
                    <Label for="" className="me-sm-2">
                      <i className="fal fa-user me-2"> </i>
                      Customer Name:
                    </Label>
                    <p>{customer.accountName}</p>
                  </Col>
                  <Col md="6" className="edit-item mb-3">
                    <Label for="" className="me-sm-2">
                      <i className="fal fa-file-alt me-2"> </i>
                      Invoice Account:
                    </Label>
                    <p>{customer.invoiceAccountNo}</p>
                  </Col>
                  <Col md="6" className="edit-item">
                    <FormGroup>
                      <Label for="classification" className="me-sm-2">Classification:</Label>
                      <Input
                        type="text"
                        name="classification"
                        id="classification"
                        value={customer.classification}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_CLASSIFICATION,
                            message: "Invalid Classification",
                          },
                        })}
                        invalid={errors.classification && errors.classification.message}
                        valid={customer.classification && errors.classification}
                      />
                      <FormFeedback>
                        {errors.classification && errors.classification.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                </div>
                <div className="edit-block">
                  <Col xs="12">
                    <h6 className="fw-bold mb-3">MozCom User</h6>
                  </Col>
                  <Col md="6" className="edit-item">
                    <FormGroup>
                      <Label for="mozcomUserName" className="me-sm-2">Name:</Label>
                      <Input
                        type="text"
                        name="mozcomUserName"
                        id="mozcomUserName"
                        value={customer.mozcomUserName}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_ALPHABETIC,
                            message: "Invalid Name",
                          },
                        })}
                        invalid={errors.mozcomUserName && errors.mozcomUserName.message}
                        valid={customer.mozcomUserName && errors.mozcomUserName}
                      />
                      <FormFeedback>
                        {errors.mozcomUserName && errors.mozcomUserName.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="6" className="edit-item">
                    <FormGroup>
                      <Label for="mozcomUserEmail" className="me-sm-2">Email:</Label>
                      <Input
                        type="text"
                        name="mozcomUserEmail"
                        id="mozcomUserEmail"
                        value={customer.mozcomUserEmail}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_EMAIL,
                            message: "Invalid email format",
                          },
                        })}
                        invalid={errors.mozcomUserEmail && errors.mozcomUserEmail.message}
                        valid={customer.mozcomUserEmail && errors.mozcomUserEmail}
                      />
                      <FormFeedback>
                        {errors.mozcomUserEmail && errors.mozcomUserEmail.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                </div>

                <div className="edit-block">
                  <Col xs="12">
                    <h6 className="fw-bold mb-3">Sales Representative (SR)</h6>
                  </Col>
                  <Col md="6" className="edit-item mb-3">
                    <FormGroup>
                      <Label for="srName" className="me-sm-2">Name:</Label>
                      <Input
                        type="text"
                        name="srName"
                        id="srName"
                        value={customer.srName}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_ALPHABETIC,
                            message: "Invalid Name",
                          },
                        })}
                        invalid={errors.srName && errors.srName.message}
                        valid={customer.srName && errors.srName}
                      />
                      <FormFeedback>
                        {errors.srName && errors.srName.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="6" className="edit-item mb-3">
                    <FormGroup>
                      <Label for="srEmail" className="me-sm-2">Email:</Label>
                      <Input
                        type="text"
                        name="srEmail"
                        id="srEmail"
                        value={customer.srEmail}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_EMAIL,
                            message: "Invalid email format",
                          },
                        })}
                        invalid={errors.srEmail && errors.srEmail.message}
                        valid={customer.srEmail && errors.srEmail}
                      />
                      <FormFeedback>{errors.srEmail && errors.srEmail.message}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="6" className="edit-item">
                    <FormGroup>
                      <Label for="srPhoneDialCode" className="me-sm-2">Dial Code:</Label>
                      <Input
                        type="text"
                        name="srPhoneDialCode"
                        id="srPhoneDialCode"
                        value={customer.srPhoneDialCode}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_DIAL_CODE,
                            message: "Invalid Dial Code",
                          },
                        })}
                        invalid={errors.srPhoneDialCode && errors.srPhoneDialCode.message}
                        valid={customer.srPhoneDialCode && errors.srPhoneDialCode}
                      />
                      <FormFeedback>
                        {errors.srPhoneDialCode && errors.srPhoneDialCode.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>

                  <Col md="6" className="edit-item">
                    <FormGroup>
                      <Label for="srPhoneNo" className="me-sm-2">Phone Number:</Label>
                      <Input
                        type="text"
                        name="srPhoneNo"
                        id="srPhoneNo"
                        value={customer.srPhoneNo}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_NUMBER,
                            message: "Invalid Phone No",
                          },
                        })}
                        invalid={errors.srPhoneNo && errors.srPhoneNo.message}
                        valid={customer.srPhoneNo && errors.srPhoneNo}
                      />
                      <FormFeedback>{errors.srPhoneNo && errors.srPhoneNo.message}</FormFeedback>
                    </FormGroup>
                  </Col>
                </div>

                <div className="edit-block last">
                  <Col xs="12">
                    <h6 className="fw-bold">Customer Service Contact (CSC)</h6>
                  </Col>
                  <Col md="6" className="edit-item mb-3">
                    <FormGroup>
                      <Label for="csrName" className="me-sm-2">Name:</Label>
                      <Input
                        type="text"
                        name="csrName"
                        id="csrName"
                        value={customer.csrName}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_ALPHABETIC,
                            message: "Invalid Name",
                          },
                        })}
                        invalid={errors.csrName && errors.csrName.message}
                        valid={customer.csrName && errors.csrName}
                      />
                      <FormFeedback>{errors.csrName && errors.csrName.message}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="6" className="edit-item mb-3">
                    <FormGroup>
                      <Label for="csrEmail" className="me-sm-2">Email:</Label>
                      <Input
                        type="text"
                        name="csrEmail"
                        id="csrEmail"
                        value={customer.csrEmail}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_EMAIL,
                            message: "Invalid email format",
                          },
                        })}
                        invalid={errors.csrEmail && errors.csrEmail.message}
                        valid={customer.csrEmail && errors.csrEmail}
                      />
                      <FormFeedback>{errors.csrEmail && errors.csrEmail.message}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="6" className="edit-item mb-3">
                    <FormGroup>
                      <Label for="csrPhoneDialCode" className="me-sm-2">Dial Code:</Label>
                      <Input
                        type="text"
                        name="csrPhoneDialCode"
                        id="csrPhoneDialCode"
                        value={customer.csrPhoneDialCode}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_DIAL_CODE,
                            message: "Invalid Dial Code",
                          },
                        })}
                        invalid={errors.csrPhoneDialCode && errors.csrPhoneDialCode.message}
                        valid={customer.csrPhoneDialCode && errors.csrPhoneDialCode}
                      />
                      <FormFeedback>
                        {errors.csrPhoneDialCode && errors.csrPhoneDialCode.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="6" className="edit-item mb-3">
                    <FormGroup>
                      <Label for="csrPhoneNo" className="me-sm-2">Phone Number:</Label>
                      <Input
                        type="text"
                        name="csrPhoneNo"
                        id="csrPhoneNo"
                        value={customer.csrPhoneNo}
                        onChange={(event) => handleEditCustomer(event)}
                        innerRef={register({
                          pattern: {
                            value: IS_NUMBER,
                            message: "Invalid Phone No ",
                          },
                        })}
                        invalid={errors.csrPhoneNo && errors.csrPhoneNo.message}
                        valid={customer.csrPhoneNo && errors.csrPhoneNo}
                      />
                      <FormFeedback>
                        {errors.csrPhoneNo && errors.csrPhoneNo.message}
                      </FormFeedback>
                    </FormGroup>
                  </Col>
                </div>
                <div className="edit-footer">
                  <Button color="light" className="me-2 btn-radius" onClick={() => handleClose()}>
                    Cancel
                  </Button>
                  <Button color="primary" className="btn-radius">
                    Save
                  </Button>
                </div>
              </fieldset>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

Edit.propTypes = {
  handleClose: PropTypes.func.isRequired,
  customer: PropTypes.arrayOf(PropTypes.any).isRequired,
  handleSave: PropTypes.func.isRequired,
  handleEditCustomer: PropTypes.func.isRequired,
  methods: PropTypes.shape({
    register: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.any),
    handleSubmit: PropTypes.func,
  }).isRequired,
  message: PropTypes.objectOf(PropTypes.any).isRequired,
  editCustomerInProgress: PropTypes.bool.isRequired,
  companyId: PropTypes.string.isRequired,
};

export default Edit;
